import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Textarea from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, Upload, Send } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { API_BASE_URL } from '@/config'

interface FileResponse {
  modelId: string
  response: string
  tokens: number
  time: number
}

export default function FileChat() {
  const [files, setFiles] = useState<File[]>([])
  const [prompt, setPrompt] = useState('')
  const [response, setResponse] = useState<FileResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedModel, setSelectedModel] = useState('gemini-1.5-pro')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setFiles(prevFiles => [...prevFiles, ...newFiles])
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleSubmit = async () => {
    if (!prompt.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a prompt',
        variant: 'destructive',
      })
      return
    }

    if (files.length === 0) {
      toast({
        title: 'Error',
        description: 'Please upload at least one file',
        variant: 'destructive',
      })
      return
    }

    setIsLoading(true)
    try {
      const formData = new FormData()
      files.forEach(file => {
        formData.append('files', file)
      })
      formData.append('prompt', prompt)
      formData.append('model', selectedModel)

      const response = await fetch(`${API_BASE_URL}/file-chat`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        
        // Handle rate limit errors
        if (response.status === 429) {
          toast({
            title: 'Rate Limit Exceeded',
            description: 'The API quota has been exceeded. Please try again later or switch to a different model.',
            variant: 'destructive',
          })
          return
        }

        // Handle other specific error cases
        if (errorData.error) {
          toast({
            title: 'Error',
            description: errorData.error,
            variant: 'destructive',
          })
          return
        }

        throw new Error('Failed to get response')
      }

      const data = await response.json()
      setResponse(data)
    } catch (error) {
      console.error('File chat error:', error)
      toast({
        title: 'Error',
        description: 'Failed to get response from the model. Please try again or switch to a different model.',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">File Chat</h1>
        <Button variant="outline" onClick={() => window.history.back()}>
          Back to Compare
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Upload Files</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                className="hidden"
              />
              <Button onClick={handleUploadClick} className="w-full">
                <Upload className="mr-2 h-4 w-4" />
                Upload Files
              </Button>
            </div>
            <div className="space-y-2">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                  <span className="text-sm truncate">{file.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFiles(files.filter((_, i) => i !== index))}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Chat</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select a model" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
              </SelectContent>
            </Select>

            <Textarea
              placeholder="Enter your prompt..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-[100px]"
            />

            <Button
              onClick={handleSubmit}
              disabled={isLoading || !prompt.trim() || files.length === 0}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {response && (
        <Card>
          <CardHeader>
            <CardTitle>Response</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="whitespace-pre-wrap">{response.response}</p>
              <div className="text-sm text-muted-foreground">
                <p>Tokens: {response.tokens}</p>
                <p>Time: {response.time}ms</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
} 